import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { createCourse } from "../api/courses";
import { ApiError } from "../api/client";

interface Props {
  onClose: () => void;
}

export function CreateCourseDialog({ onClose }: Props) {
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [isMandatory, setIsMandatory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Auto-generate slug from title until user touches the slug field
  const [slugTouched, setSlugTouched] = useState(false);

  function handleTitleChange(value: string) {
    setTitle(value);
    if (!slugTouched) {
      setSlug(
        value
          .toLowerCase()
          .replace(/[ё]/g, "yo")
          .replace(/[а-я]/g, (c) => {
            const map: Record<string, string> = {
              а: "a", б: "b", в: "v", г: "g", д: "d", е: "e",
              ж: "zh", з: "z", и: "i", й: "y", к: "k", л: "l", м: "m",
              н: "n", о: "o", п: "p", р: "r", с: "s", т: "t", у: "u",
              ф: "f", х: "h", ц: "c", ч: "ch", ш: "sh", щ: "sch",
              ъ: "", ы: "y", ь: "", э: "e", ю: "yu", я: "ya",
            };
            return map[c] ?? c;
          })
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, ""),
      );
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const course = await createCourse({
        title: title.trim(),
        slug: slug.trim(),
        description: description.trim() || undefined,
        is_mandatory: isMandatory,
      });
      onClose();
      navigate(`/courses/${course.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "SLUG_TAKEN") setError("Такой slug уже занят");
        else if (err.status === 403) setError("Нет прав на создание курса");
        else if (err.status === 422) setError("Проверь поля формы");
        else setError(`Ошибка ${err.status}`);
      } else {
        setError("Сеть недоступна");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md space-y-4 rounded-lg bg-white p-6 shadow-lg"
      >
        <header className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Новый курс</h2>
            <p className="text-sm text-slate-500">
              Будет создан в статусе «черновик».
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Закрыть"
          >
            ×
          </button>
        </header>

        <div className="space-y-1">
          <label htmlFor="title" className="block text-sm font-medium text-slate-700">
            Название
          </label>
          <input
            id="title"
            type="text"
            required
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>

        <div className="space-y-1">
          <label htmlFor="slug" className="block text-sm font-medium text-slate-700">
            Slug (латиница, цифры, дефисы)
          </label>
          <input
            id="slug"
            type="text"
            required
            pattern="[a-z0-9\-]+"
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugTouched(true);
            }}
            className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
          <p className="text-xs text-slate-500">Используется в URL. После публикации не меняется.</p>
        </div>

        <div className="space-y-1">
          <label htmlFor="desc" className="block text-sm font-medium text-slate-700">
            Описание (опционально)
          </label>
          <textarea
            id="desc"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded border border-slate-300 px-3 py-2 text-sm focus:border-brand-accent focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>

        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={isMandatory}
            onChange={(e) => setIsMandatory(e.target.checked)}
          />
          Обязательный курс
        </label>

        {error && (
          <div role="alert" className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary"
          >
            {submitting ? "Создаём…" : "Создать"}
          </button>
        </div>
      </form>
    </div>
  );
}
